import { BLACKOUT_SCOPES } from "helpers/blackouts"

/**
 * Turns a day from the blackout feed into the props its label chip needs, on any surface.
 *
 * One blackout shows its own label ("Business Blackout", "Crew OFF"). Several show
 * "Partial Blackout" with the count, unless one of them closes the whole day — then the
 * full-day closure is what the guide needs to see.
 *
 * `label` is server copy and is rendered as-is; `labelKey` is set only when the label is
 * composed on the client and must go through `t()`.
 */
export const getDayChipProps = (day) => {
  const blackouts = day?.blackouts || []
  if (!blackouts.length) return null

  if (day.status === "full") {
    const closingBlackout =
      blackouts.find((blackout) => blackout.scope === BLACKOUT_SCOPES.BUSINESS && !blackout.start_time && !blackout.end_time) ||
      blackouts[0]
    return { label: closingBlackout.label, variant: "full", count: blackouts.length }
  }

  if (blackouts.length === 1) {
    return { label: blackouts[0].label, variant: "partial", count: 1 }
  }

  return {
    labelKey: "calendar.blackout.partial",
    labelDefault: "Partial Blackout",
    variant: "partial",
    count: blackouts.length
  }
}

/** True when the day carries something the guide has to act on. */
export const hasBlackoutAction = (day) => Boolean(day?.bookings_requiring_action)

/** Two days belong to the same banner run when exactly the same blackouts cover both. */
const runKey = (day) =>
  (day?.blackouts || [])
    .map((blackout) => blackout.id)
    .sort()
    .join("|")

/**
 * Collapses a week into banner segments so a blackout spanning several days renders as one
 * bar across those columns, as the week design shows, instead of repeating per column.
 *
 * @param {string[]} dayKeys the week's days, in order, as "YYYY-MM-DD"
 * @param {(dateKey: string) => Object|null} getDay
 * @returns {{dateKey: string, span: number, day: Object|null}[]} one entry per segment
 */
export const buildBlackoutRuns = (dayKeys, getDay) =>
  dayKeys.reduce((runs, dateKey) => {
    const day = getDay(dateKey)
    const key = runKey(day)
    const previous = runs[runs.length - 1]

    // Clear days never merge — each keeps its own empty cell so the columns stay aligned.
    if (key && previous && runKey(previous.day) === key) {
      previous.span += 1
      return runs
    }

    runs.push({ dateKey, span: 1, day: key ? day : null })
    return runs
  }, [])
